/**
 * The machine capacity view model (#483).
 *
 * One deterministic shape per connected computer, built from rows Brunel already
 * stores: `brunel.node_presence`, `brunel.node_pressure`, `brunel.node_capacity`,
 * `brunel.node_resources`, `brunel.node_versions`, the sessions/workers/assignments
 * a node holds, and the local runtime status a node already reports. No I/O, no
 * clock, no second ledger: the moment arrives as `nowMs` and every figure is traced
 * to a row.
 *
 * The one rule that matters more than the rest: a reading a computer cannot take is
 * `null`, and `null` renders as "Unknown". It never becomes a zero, a full meter, an
 * empty meter, or a reason to hold a machine back. Everything downstream - the
 * compact cards, the detail surface, Resource Health, and the Engine Room (#484) -
 * consumes this module rather than re-deriving any of it.
 */

import {
  DEFAULT_CAPACITY_GUARD_CONTRACT,
  capacityProfileValues,
  resolveCapacityProfile,
  type CapacityGuardContract,
  type CapacityProfileKey,
  type CapacityProfileValues,
  type MachineCapacityLimits,
} from './profiles.js';
import { DISK_MIN_FREE_BYTES, DISK_MIN_FREE_PERCENT } from './disk-guard.js';

/** A row of `brunel.node_presence`. */
export interface MachinePresenceRow {
  readonly node_id: string;
  readonly name: string;
  readonly status: string | null;
  readonly last_seen_at: string | null;
}

/** A row of `brunel.node_capacity`, including the columns this lane adds. */
export interface MachineCapacityRow {
  readonly node_id: string;
  readonly name: string;
  readonly worker_seats: number | null;
  readonly reserve_memory_percent: number | null;
  readonly reserve_graphics_percent: number | null;
  readonly cpu_ceiling_percent: number | null;
  readonly graphics_ceiling_percent: number | null;
  readonly observed_at: string | null;
  readonly memory_free_percent: number | null;
  readonly video_memory_free_percent: number | null;
  readonly active_runs: number | null;
  readonly cpu_sustained_percent: number | null;
  readonly cpu_sample_count: number | null;
  readonly cpu_guard_engaged: boolean | null;
  readonly cpu_guard_changed_at: string | null;
  readonly graphics_sustained_percent: number | null;
  readonly graphics_sample_count: number | null;
  readonly graphics_guard_engaged: boolean | null;
  readonly graphics_guard_changed_at: string | null;
  readonly accepting: boolean | null;
  readonly holding_back: string | null;
}

/** A row of `brunel.node_pressure`: the newest sample a computer left behind. */
export interface MachinePressureRow {
  readonly node_id: string;
  readonly observed_at: string | null;
  readonly cpu_count: number | null;
  readonly cpu_load_percent: number | null;
  readonly memory_total_bytes: number | null;
  readonly memory_free_bytes: number | null;
  readonly agent_memory_bytes: number | null;
  readonly memory_free_percent: number | null;
  readonly graphics_count: number | null;
  readonly graphics_util_percent: number | null;
  readonly video_memory_total_bytes: number | null;
  readonly video_memory_used_bytes: number | null;
  readonly video_memory_free_percent: number | null;
  readonly active_runs: number | null;
}

/** A row of `brunel.node_versions`: what the node agent is running. */
export interface MachineVersionRow {
  readonly node_id: string;
  readonly running_version: string | null;
  readonly target_version: string | null;
  readonly behind: boolean | null;
}

/** One session on a machine, with the Worker holding it. */
export interface MachineSessionRow {
  readonly node_id: string;
  readonly worker_id: string;
  readonly status: string | null;
  readonly lifecycle: string | null;
}

/** One Worker, as the Workers table knows it. */
export interface MachineWorkerRow {
  readonly id: string;
  readonly number: number | null;
  readonly name: string | null;
  readonly status: string | null;
}

/** One task a machine is running right now, from the existing running-task read. */
export interface MachineRunningRow {
  readonly node_id: string | null;
  readonly worker_id: string | null;
  readonly title: string | null;
  readonly started_at: string | null;
}

/** One recently finished run on a machine, from the existing finished-run read. */
export interface MachineFinishedRow {
  readonly node_id: string | null;
  readonly reported_at: string | null;
  readonly success: boolean | null;
}

/** One local-runtime probe a node already publishes, whichever runtime that is. */
export interface MachineLocalRuntimeRow {
  readonly node_id: string;
  readonly connection_key: string;
  readonly service_state: string | null;
  readonly runtime_version: string | null;
  readonly loaded_models_known: boolean | null;
  readonly loaded_models: readonly { readonly model_key?: string | null; readonly name?: string | null }[] | null;
  readonly observed_at: string | null;
}

/** One sample of `brunel.node_resources`, for the small history the detail shows. */
export interface MachineSampleRow {
  readonly node_id: string;
  readonly observed_at: string;
  readonly cpu_load_percent: number | null;
  readonly memory_total_bytes: number | null;
  readonly memory_free_bytes: number | null;
  readonly graphics_util_percent: number | null;
  readonly video_memory_total_bytes: number | null;
  readonly video_memory_used_bytes: number | null;
}

/** One `node.limits_set` audit event, so the detail can show who changed what. */
export interface MachineLimitAuditRow {
  readonly node_id: string | null;
  readonly occurred_at: string | null;
  readonly actor_id: string | null;
  readonly before: Partial<MachineCapacityLimits> | null;
  readonly after: Partial<MachineCapacityLimits> | null;
}

export type MachinePresence = 'connected' | 'stale' | 'disconnected' | 'unknown';
export type MachineMeterTone = 'ok' | 'attention' | 'unknown';

/** Which capacity dimensions exist. `seats` is a count; the rest are percentages. */
export type MachineMeterKey = 'seats' | 'cpu' | 'memory' | 'graphics' | 'video_memory';

/**
 * One small meter on a card. `percent` is what to fill, and `null` fills nothing
 * because nothing is known - an empty meter and an Unknown meter are not the same
 * thing, so `known` says which this is.
 */
export interface MachineMeter {
  readonly key: MachineMeterKey;
  readonly label: string;
  readonly percent: number | null;
  readonly text: string;
  readonly detail: string | null;
  readonly tone: MachineMeterTone;
  readonly known: boolean;
  /** The owner's threshold on this dimension, when one applies to it. */
  readonly limitPercent: number | null;
  readonly limitText: string | null;
}

/** Whether this machine is being given new work, and if not, why not. */
export type DispatchStance = 'accepting' | 'at_seat_limit' | 'held_back' | 'unknown';

/** Which reading is holding a machine back. */
export type HoldBackDimension = 'memory' | 'video_memory' | 'cpu' | 'graphics';

/** The state of one sustained guard, exactly as the engine last evaluated it. */
export interface MachineGuardState {
  readonly dimension: 'cpu' | 'graphics';
  readonly label: string;
  readonly ceilingPercent: number | null;
  readonly sustainedPercent: number | null;
  readonly sampleCount: number | null;
  readonly engaged: boolean;
  readonly changedAt: string | null;
  /** True when the ceiling is set but the window has too few readings to judge. */
  readonly unmeasured: boolean;
  readonly line: string;
}

/** One point of the small recent history the detail draws. */
export interface MachineHistoryPoint {
  readonly at: string;
  readonly cpuPercent: number | null;
  readonly memoryUsedPercent: number | null;
  readonly graphicsPercent: number | null;
  readonly videoMemoryUsedPercent: number | null;
}

/** One Worker seat on this machine and what it is doing. */
export interface MachineSeat {
  readonly workerId: string;
  readonly workerNumber: number | null;
  readonly label: string;
  readonly status: string;
  readonly running: boolean;
  readonly taskTitle: string | null;
  readonly startedAt: string | null;
}

/**
 * One moment this machine was over an owner limit, read back out of the samples
 * Brunel already keeps. Not a new ledger: the same `brunel.node_resources` rows
 * the meters are built from, compared against the limits in force now.
 */
export interface MachineHoldBackEvent {
  readonly at: string;
  readonly dimension: HoldBackDimension;
  readonly detail: string;
}

/** Everything known about one computer's capacity. The stable shape #484 consumes. */
export interface MachineCapacity {
  readonly nodeId: string;
  readonly name: string;

  readonly presence: MachinePresence;
  readonly presenceLabel: string;
  readonly lastSeenAt: string | null;

  readonly agentVersion: string | null;
  readonly targetVersion: string | null;
  readonly versionCurrent: boolean | null;
  readonly versionLine: string;

  readonly seatsConfigured: number | null;
  readonly seatsActive: number | null;
  readonly seatsFree: number | null;
  readonly seatsText: string;
  readonly seats: readonly MachineSeat[];

  readonly stance: DispatchStance;
  /** "Yes", "Held back", "At its seat limit", "Unknown" - the card's own word. */
  readonly stanceLabel: string;
  readonly holdBack: HoldBackDimension | null;
  readonly holdBackReason: string | null;
  /** Why new work is or is not going to this machine, in one sentence. */
  readonly dispatchLine: string;

  readonly meters: readonly MachineMeter[];
  readonly guards: readonly MachineGuardState[];

  readonly activeRuns: number | null;
  /** Runs this machine finished inside the recent window; null when unreadable. */
  readonly recentRuns: number | null;
  readonly recentWindowMinutes: number;

  readonly observedAt: string | null;
  readonly readingAgeMs: number | null;
  readonly readingAgeLabel: string | null;
  readonly stale: boolean;

  readonly limits: MachineCapacityLimits;
  readonly profile: CapacityProfileKey;
  readonly profileValues: CapacityProfileValues | null;

  readonly agentMemoryBytes: number | null;
  readonly cpuCount: number | null;
  readonly localRuntimes: readonly MachineLocalRuntime[];

  readonly history: readonly MachineHistoryPoint[];
  readonly holdBackEvents: readonly MachineHoldBackEvent[];
  readonly limitChanges: readonly MachineLimitAuditRow[];

  /** True when this machine is within a few points of one of the owner's limits. */
  readonly nearingLimit: boolean;
  readonly nearingLine: string | null;
}

/** A local runtime this machine reports, and what it currently holds. */
export interface MachineLocalRuntime {
  readonly connectionKey: string;
  readonly label: string;
  readonly state: string;
  readonly version: string | null;
  readonly loadedModels: readonly string[] | null;
  readonly observedAt: string | null;
  readonly line: string;
}

export interface MachineCapacityInput {
  readonly nowMs: number;
  readonly nodes: readonly MachinePresenceRow[];
  readonly capacity?: readonly MachineCapacityRow[];
  readonly pressure?: readonly MachinePressureRow[];
  readonly versions?: readonly MachineVersionRow[];
  readonly sessions?: readonly MachineSessionRow[];
  readonly workers?: readonly MachineWorkerRow[];
  readonly running?: readonly MachineRunningRow[];
  readonly finished?: readonly MachineFinishedRow[];
  readonly localRuntimes?: readonly MachineLocalRuntimeRow[];
  readonly samples?: readonly MachineSampleRow[];
  readonly limitChanges?: readonly MachineLimitAuditRow[];
  readonly contract?: CapacityGuardContract;
  /** How stale a reading has to be before the cards say so. */
  readonly staleAfterMs?: number;
  /** How far back "recent" reaches for the workload indicator. */
  readonly recentWindowMs?: number;
  /** How many history points the detail keeps per machine, newest last. */
  readonly historyPoints?: number;
  /**
   * Owner-facing names for local runtime connection keys, from the caller's existing
   * connection catalog. A key with no entry is shown as the key itself rather than renamed.
   */
  readonly runtimeLabels?: Readonly<Record<string, string>>;
  /** False where a read failed: a failed read is Unknown, not an empty truth. */
  readonly available?: Readonly<{
    capacity?: boolean;
    pressure?: boolean;
    versions?: boolean;
    sessions?: boolean;
    running?: boolean;
    finished?: boolean;
    samples?: boolean;
    localRuntimes?: boolean;
  }>;
}

export const READING_STALE_AFTER_MS = 300_000;
export const PRESENCE_STALE_AFTER_MS = 180_000;
const RECENT_WINDOW_MS = 60 * 60_000;
const HISTORY_POINTS = 24;
const HOLD_BACK_EVENTS = 8;
/** How close to a limit counts as "nearing" it, in percentage points. */
export const NEARING_LIMIT_POINTS = 5;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** "under a minute old", "4m old", "3h old", "2d old" - only about a reading that exists. */
export function readingAgeLabel(ageMs: number | null): string | null {
  if (ageMs === null) return null;
  if (ageMs < MINUTE_MS) return 'under a minute old';
  if (ageMs < HOUR_MS) return `${Math.floor(ageMs / MINUTE_MS)}m old`;
  if (ageMs < DAY_MS) return `${Math.floor(ageMs / HOUR_MS)}h old`;
  return `${Math.floor(ageMs / DAY_MS)}d old`;
}

function ageMsOf(at: string | null, nowMs: number): number | null {
  if (at === null) return null;
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? null : nowMs - parsed;
}

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function usedFromFree(freePercent: number | null): number | null {
  return freePercent === null ? null : Math.max(0, Math.min(100, 100 - freePercent));
}

function usedPercentFromBytes(total: number | null, used: number | null): number | null {
  const t = finite(total);
  const u = finite(used);
  if (t === null || u === null || t <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((u / t) * 100)));
}

const HOLD_BACK_NAMES: Readonly<Record<HoldBackDimension, string>> = {
  memory: 'free memory',
  video_memory: 'free video memory',
  cpu: 'sustained processor load',
  graphics: 'sustained graphics load',
};

/** The owner's words for a dimension the engine named by its column. */
export function holdBackName(dimension: string): string {
  return HOLD_BACK_NAMES[dimension as HoldBackDimension] ?? dimension.replaceAll('_', ' ');
}

/** Only the four dimensions the engine can actually name count as a named reading. */
export function holdBackDimension(value: string | null): HoldBackDimension | null {
  return value === 'memory' || value === 'video_memory' || value === 'cpu' || value === 'graphics'
    ? value : null;
}

function presenceOf(row: MachinePresenceRow, nowMs: number): MachinePresence {
  if (row.status === 'offline') return 'disconnected';
  const age = ageMsOf(row.last_seen_at, nowMs);
  if (age === null) return 'unknown';
  return age > PRESENCE_STALE_AFTER_MS ? 'stale' : 'connected';
}

const PRESENCE_LABELS: Readonly<Record<MachinePresence, string>> = {
  connected: 'online',
  stale: 'stale',
  disconnected: 'offline',
  unknown: 'unknown',
};

function meter(
  key: MachineMeterKey,
  label: string,
  percent: number | null,
  limitPercent: number | null,
  detail: string | null,
  breached: boolean,
): MachineMeter {
  const known = percent !== null;
  return {
    key,
    label,
    percent,
    text: known ? `${Math.round(percent)}%` : 'Unknown',
    detail,
    tone: !known ? 'unknown' : breached ? 'attention' : 'ok',
    known,
    limitPercent,
    limitText: limitPercent === null ? null : `${limitPercent}%`,
  };
}

function seatMeter(active: number | null, configured: number | null): MachineMeter {
  const known = active !== null && configured !== null && configured > 0;
  const percent = known ? Math.min(100, Math.round((active / configured) * 100)) : null;
  return {
    key: 'seats',
    label: 'Workers',
    percent,
    text: known ? `${active} / ${configured}` : 'Unknown',
    detail: known ? `${configured} Worker seat${configured === 1 ? '' : 's'} configured` : null,
    tone: !known ? 'unknown' : active >= configured ? 'attention' : 'ok',
    known,
    limitPercent: null,
    limitText: known ? `${configured} seats` : null,
  };
}

function guardState(
  dimension: 'cpu' | 'graphics',
  ceiling: number | null,
  sustained: number | null,
  samples: number | null,
  engaged: boolean,
  changedAt: string | null,
  contract: CapacityGuardContract,
): MachineGuardState {
  const label = dimension === 'cpu' ? 'Processor ceiling' : 'Graphics ceiling';
  const enough = samples !== null && samples >= contract.minSamples;
  const unmeasured = ceiling !== null && (sustained === null || !enough);
  const minutes = Math.round(contract.windowSeconds / 6) / 10;
  let line: string;
  if (ceiling === null) {
    line = `${label} is off, so load never holds this machine back.`;
  } else if (unmeasured) {
    line = `${label} is ${ceiling}%, but this machine has not reported enough readings in the last ${minutes} minutes to judge it. Unknown holds nothing back.`;
  } else if (engaged) {
    line = `Held back: ${sustained}% sustained over the last ${minutes} minutes, at or above the ${ceiling}% ceiling. New work waits; work already running carries on.`;
  } else {
    line = `${sustained}% sustained over the last ${minutes} minutes, under the ${ceiling}% ceiling.`;
  }
  return {
    dimension,
    label,
    ceilingPercent: ceiling,
    sustainedPercent: unmeasured ? null : sustained,
    sampleCount: samples,
    engaged,
    changedAt,
    unmeasured,
    line,
  };
}

function localRuntimeOf(
  row: MachineLocalRuntimeRow,
  labels: Readonly<Record<string, string>>,
): MachineLocalRuntime {
  // The owner-facing name comes from the caller's existing connection catalog, never from
  // a runtime name written into this module.
  const label = labels[row.connection_key] ?? row.connection_key;
  const state = row.service_state === null || row.service_state === '' ? 'unknown' : row.service_state;
  const known = row.loaded_models_known === true;
  const models = known
    ? (row.loaded_models ?? []).map((model) => model.model_key ?? model.name ?? '').filter((name) => name !== '')
    : null;
  const loaded = models === null
    ? 'loaded models Unknown'
    : models.length === 0 ? 'no model loaded' : `loaded: ${models.join(', ')}`;
  return {
    connectionKey: row.connection_key,
    label,
    state,
    version: row.runtime_version,
    loadedModels: models,
    observedAt: row.observed_at,
    line: `${label} ${state} · ${loaded}`,
  };
}

function by<T>(rows: readonly T[], key: (row: T) => string | null): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const id = key(row);
    if (id === null) continue;
    const list = out.get(id);
    if (list === undefined) out.set(id, [row]); else list.push(row);
  }
  return out;
}

function first<T extends { readonly node_id: string }>(rows: readonly T[]): Map<string, T> {
  const out = new Map<string, T>();
  for (const row of rows) if (!out.has(row.node_id)) out.set(row.node_id, row);
  return out;
}

/**
 * Every machine, in the order the presence read returned them. One pass, no I/O,
 * and a machine that appears only in the presence list still gets a row that says
 * honestly that nothing else is known about it.
 */
export function buildMachineCapacity(input: MachineCapacityInput): readonly MachineCapacity[] {
  const contract = input.contract ?? DEFAULT_CAPACITY_GUARD_CONTRACT;
  const staleAfterMs = input.staleAfterMs ?? READING_STALE_AFTER_MS;
  const recentWindowMs = input.recentWindowMs ?? RECENT_WINDOW_MS;
  const historyPoints = input.historyPoints ?? HISTORY_POINTS;
  const has = (key: keyof NonNullable<MachineCapacityInput['available']>): boolean =>
    input.available?.[key] !== false;

  const capacityByNode = first(has('capacity') ? (input.capacity ?? []) : []);
  const pressureByNode = first(has('pressure') ? (input.pressure ?? []) : []);
  const versionByNode = first(has('versions') ? (input.versions ?? []) : []);
  const sessionsByNode = by(has('sessions') ? (input.sessions ?? []) : [], (row) => row.node_id);
  const runningByNode = by(has('running') ? (input.running ?? []) : [], (row) => row.node_id);
  const finishedByNode = by(has('finished') ? (input.finished ?? []) : [], (row) => row.node_id);
  const runtimesByNode = by(has('localRuntimes') ? (input.localRuntimes ?? []) : [], (row) => row.node_id);
  const samplesByNode = by(has('samples') ? (input.samples ?? []) : [], (row) => row.node_id);
  const changesByNode = by(input.limitChanges ?? [], (row) => row.node_id);
  const workerById = new Map((input.workers ?? []).map((worker) => [worker.id, worker]));

  return input.nodes.map((node) => {
    const room = capacityByNode.get(node.node_id) ?? null;
    const sample = pressureByNode.get(node.node_id) ?? null;
    const version = versionByNode.get(node.node_id) ?? null;

    const limits: MachineCapacityLimits = {
      workerSeats: finite(room?.worker_seats ?? null),
      reserveMemoryPercent: finite(room?.reserve_memory_percent ?? null) ?? 0,
      reserveGraphicsPercent: finite(room?.reserve_graphics_percent ?? null) ?? 0,
      cpuCeilingPercent: finite(room?.cpu_ceiling_percent ?? null),
      graphicsCeilingPercent: finite(room?.graphics_ceiling_percent ?? null),
    };
    const profile = room === null ? 'custom' : resolveCapacityProfile(limits);

    const observedAt = room?.observed_at ?? sample?.observed_at ?? null;
    const readingAgeMs = ageMsOf(observedAt, input.nowMs);
    const stale = readingAgeMs !== null && readingAgeMs > staleAfterMs;

    const seatsConfigured = finite(room?.worker_seats ?? null);
    const activeRuns = finite(room?.active_runs ?? sample?.active_runs ?? null);
    const seatsFree = seatsConfigured === null || activeRuns === null || seatsConfigured < 0
      ? null : Math.max(0, seatsConfigured - activeRuns);

    // Seats: one line per Worker this machine holds, with the task it is running.
    const runningHere = runningByNode.get(node.node_id) ?? [];
    const seats: MachineSeat[] = [];
    const seenWorkers = new Set<string>();
    for (const session of sessionsByNode.get(node.node_id) ?? []) {
      if (seenWorkers.has(session.worker_id)) continue;
      if (session.lifecycle !== null && session.lifecycle !== 'active' && session.lifecycle !== 'restored') continue;
      seenWorkers.add(session.worker_id);
      const worker = workerById.get(session.worker_id) ?? null;
      const run = runningHere.find((row) => row.worker_id === session.worker_id) ?? null;
      seats.push({
        workerId: session.worker_id,
        workerNumber: finite(worker?.number ?? null),
        label: worker?.name ?? (worker?.number != null ? `Worker ${worker.number}` : 'Worker'),
        status: worker?.status ?? session.status ?? 'unknown',
        running: run !== null,
        taskTitle: run?.title ?? null,
        startedAt: run?.started_at ?? null,
      });
    }
    seats.sort((a, b) => (a.workerNumber ?? 0) - (b.workerNumber ?? 0));

    // Recent workload, from the runs Brunel already recorded. Not a second ledger:
    // the same finished-run rows the Results surface reads.
    const recentRuns = has('finished')
      ? (finishedByNode.get(node.node_id) ?? []).filter((row) => {
        const age = ageMsOf(row.reported_at, input.nowMs);
        return age !== null && age <= recentWindowMs;
      }).length
      : null;

    const memoryUsed = usedFromFree(finite(room?.memory_free_percent ?? sample?.memory_free_percent ?? null));
    const videoUsed = usedFromFree(finite(room?.video_memory_free_percent ?? sample?.video_memory_free_percent ?? null));
    const cpuNow = finite(sample?.cpu_load_percent ?? null);
    const graphicsNow = finite(sample?.graphics_util_percent ?? null);

    const cpuGuard = guardState(
      'cpu', limits.cpuCeilingPercent,
      finite(room?.cpu_sustained_percent ?? null), finite(room?.cpu_sample_count ?? null),
      room?.cpu_guard_engaged === true, room?.cpu_guard_changed_at ?? null, contract,
    );
    const graphicsGuard = guardState(
      'graphics', limits.graphicsCeilingPercent,
      finite(room?.graphics_sustained_percent ?? null), finite(room?.graphics_sample_count ?? null),
      room?.graphics_guard_engaged === true, room?.graphics_guard_changed_at ?? null, contract,
    );

    const memoryFree = finite(room?.memory_free_percent ?? sample?.memory_free_percent ?? null);
    const videoFree = finite(room?.video_memory_free_percent ?? sample?.video_memory_free_percent ?? null);
    const meters: MachineMeter[] = [
      seatMeter(activeRuns, seatsConfigured),
      meter('cpu', 'CPU', cpuNow, limits.cpuCeilingPercent,
        cpuGuard.sustainedPercent === null ? null : `${cpuGuard.sustainedPercent}% sustained`,
        cpuGuard.engaged),
      meter('memory', 'RAM', memoryUsed, 100 - limits.reserveMemoryPercent,
        memoryFree === null ? null : `${memoryFree}% free, ${limits.reserveMemoryPercent}% kept for you`,
        memoryFree !== null && memoryFree < limits.reserveMemoryPercent),
      meter('graphics', 'GPU', graphicsNow, limits.graphicsCeilingPercent,
        graphicsGuard.sustainedPercent === null ? null : `${graphicsGuard.sustainedPercent}% sustained`,
        graphicsGuard.engaged),
      meter('video_memory', 'VRAM', videoUsed, 100 - limits.reserveGraphicsPercent,
        videoFree === null ? null : `${videoFree}% free, ${limits.reserveGraphicsPercent}% kept for you`,
        videoFree !== null && videoFree < limits.reserveGraphicsPercent),
    ];

    // Stance. A full machine is said to be full before anything is said about its
    // readings, because a full machine is given no further Worker whatever they say.
    const holdBack = holdBackDimension(room?.holding_back ?? null);
    let stance: DispatchStance;
    let stanceLabel: string;
    let holdBackReason: string | null = null;
    if (room === null || (room.accepting === null && holdBack === null && observedAt === null && seatsFree === null)) {
      stance = 'unknown';
      stanceLabel = 'Unknown';
    } else if (seatsFree === 0) {
      stance = 'at_seat_limit';
      stanceLabel = seatsConfigured === 0 ? 'Viewer/control only' : 'At its seat limit';
    } else if (holdBack !== null) {
      stance = 'held_back';
      stanceLabel = 'Held back';
      holdBackReason = reasonFor(holdBack, limits, memoryFree, videoFree, cpuGuard, graphicsGuard);
    } else if (room.accepting === true) {
      stance = 'accepting';
      stanceLabel = 'Yes';
    } else if (room.accepting === false) {
      stance = 'held_back';
      stanceLabel = 'Held back';
      // The disk guard is Brunel's own floor, not one of the owner's limits, and is not one
      // of the metered dimensions above: it is named here so it is never called a limit
      // the owner set.
      holdBackReason = room.holding_back === 'disk'
        ? `free disk space is under the ${DISK_MIN_FREE_BYTES / (1024 ** 3)} GB and ${DISK_MIN_FREE_PERCENT}% Brunel keeps free`
        : 'this machine is over one of the limits you set';
    } else {
      stance = 'unknown';
      stanceLabel = 'Unknown';
    }

    const ageLabel = readingAgeLabel(readingAgeMs);
    const olderThan = stale && ageLabel !== null ? ` The reading behind this is ${ageLabel}.` : '';
    let dispatchLine: string;
    if (stance === 'at_seat_limit') {
      dispatchLine = seatsConfigured === 0
        ? 'Viewer/control only: this machine has zero Worker seats and cannot take new work.'
        : seatsConfigured === null
        ? 'Busy: every Worker seat on this machine is taken. New work waits for one to free up.'
        : `Busy: ${activeRuns ?? 0} of ${seatsConfigured} Worker seat${seatsConfigured === 1 ? '' : 's'} in use. New work waits for one to free up.`;
    } else if (stance === 'held_back') {
      dispatchLine = `Held back: ${holdBackReason}. New work waits or goes to another eligible machine; work already running carries on.${olderThan}`;
    } else if (stance === 'accepting') {
      const parts = [
        seatsFree === null || seatsConfigured === null
          ? null : `${seatsFree} of ${seatsConfigured} seat${seatsConfigured === 1 ? '' : 's'} free`,
        memoryFree === null ? null : `${memoryFree - limits.reserveMemoryPercent} points of memory above your reserve`,
        cpuNow === null ? null : `${cpuNow}% of its processors in use`,
      ].filter((part): part is string => part !== null);
      dispatchLine = parts.length === 0
        ? `Taking new work.${olderThan}` : `Taking new work: ${parts.join(', ')}.${olderThan}`;
    } else {
      dispatchLine = 'This machine has not reported what it is under, so whether it can take more work is Unknown.';
    }

    // Nearing a limit: a real measurement within a few points of a real limit.
    const nearing: string[] = [];
    if (memoryFree !== null && memoryFree >= limits.reserveMemoryPercent
      && memoryFree - limits.reserveMemoryPercent <= NEARING_LIMIT_POINTS) {
      nearing.push(`free memory is ${memoryFree}%, close to the ${limits.reserveMemoryPercent}% you keep`);
    }
    if (videoFree !== null && limits.reserveGraphicsPercent > 0 && videoFree >= limits.reserveGraphicsPercent
      && videoFree - limits.reserveGraphicsPercent <= NEARING_LIMIT_POINTS) {
      nearing.push(`free video memory is ${videoFree}%, close to the ${limits.reserveGraphicsPercent}% you keep`);
    }
    for (const guard of [cpuGuard, graphicsGuard]) {
      if (guard.ceilingPercent === null || guard.engaged || guard.sustainedPercent === null) continue;
      if (guard.ceilingPercent - guard.sustainedPercent <= NEARING_LIMIT_POINTS) {
        nearing.push(`${holdBackName(guard.dimension)} is ${guard.sustainedPercent}%, close to your ${guard.ceilingPercent}% ceiling`);
      }
    }
    if (seatsFree === 1 && seatsConfigured !== null && seatsConfigured > 1) {
      nearing.push('one Worker seat left');
    }

    const samples = [...(samplesByNode.get(node.node_id) ?? [])]
      .filter((row) => Number.isFinite(Date.parse(row.observed_at)))
      .sort((a, b) => Date.parse(a.observed_at) - Date.parse(b.observed_at));
    const history: MachineHistoryPoint[] = samples.slice(-historyPoints).map((row) => ({
      at: row.observed_at,
      cpuPercent: finite(row.cpu_load_percent),
      memoryUsedPercent: usedPercentFromBytes(
        row.memory_total_bytes,
        finite(row.memory_total_bytes) === null || finite(row.memory_free_bytes) === null
          ? null : (row.memory_total_bytes as number) - (row.memory_free_bytes as number),
      ),
      graphicsPercent: finite(row.graphics_util_percent),
      videoMemoryUsedPercent: usedPercentFromBytes(row.video_memory_total_bytes, row.video_memory_used_bytes),
    }));

    const holdBackEvents = historyHoldBacks(samples, limits).slice(-HOLD_BACK_EVENTS).reverse();

    return {
      nodeId: node.node_id,
      name: node.name,
      presence: presenceOf(node, input.nowMs),
      presenceLabel: PRESENCE_LABELS[presenceOf(node, input.nowMs)],
      lastSeenAt: node.last_seen_at,
      agentVersion: version?.running_version ?? null,
      targetVersion: version?.target_version ?? null,
      versionCurrent: version === null ? null : version.behind === null ? null : !version.behind,
      versionLine: versionLineOf(version),
      seatsConfigured,
      seatsActive: activeRuns,
      seatsFree,
      seatsText: seatsConfigured === null || activeRuns === null
        ? 'Unknown' : `${activeRuns} / ${seatsConfigured}`,
      seats,
      stance,
      stanceLabel,
      holdBack,
      holdBackReason,
      dispatchLine,
      meters,
      guards: [cpuGuard, graphicsGuard],
      activeRuns,
      recentRuns,
      recentWindowMinutes: Math.round(recentWindowMs / MINUTE_MS),
      observedAt,
      readingAgeMs,
      readingAgeLabel: ageLabel,
      stale,
      limits,
      profile,
      profileValues: capacityProfileValues(profile),
      agentMemoryBytes: finite(sample?.agent_memory_bytes ?? null),
      cpuCount: finite(sample?.cpu_count ?? null),
      localRuntimes: (runtimesByNode.get(node.node_id) ?? [])
        .map((row) => localRuntimeOf(row, input.runtimeLabels ?? {})),
      history,
      holdBackEvents,
      limitChanges: changesByNode.get(node.node_id) ?? [],
      nearingLimit: nearing.length > 0,
      nearingLine: nearing.length === 0 ? null : nearing.join('; '),
    };
  });
}

function reasonFor(
  dimension: HoldBackDimension,
  limits: MachineCapacityLimits,
  memoryFree: number | null,
  videoFree: number | null,
  cpuGuard: MachineGuardState,
  graphicsGuard: MachineGuardState,
): string {
  if (dimension === 'memory') {
    return memoryFree === null
      ? 'free memory is under the share you keep for yourself'
      : `free memory is ${memoryFree}%, under the ${limits.reserveMemoryPercent}% you keep for yourself`;
  }
  if (dimension === 'video_memory') {
    return videoFree === null
      ? 'free video memory is under the share you keep for yourself'
      : `free video memory is ${videoFree}%, under the ${limits.reserveGraphicsPercent}% you keep for yourself`;
  }
  const guard = dimension === 'cpu' ? cpuGuard : graphicsGuard;
  return guard.sustainedPercent === null || guard.ceilingPercent === null
    ? `${holdBackName(dimension)} is over the ceiling you set`
    : `${holdBackName(dimension)} has stayed at ${guard.sustainedPercent}%, at or above your ${guard.ceilingPercent}% ceiling`;
}

function versionLineOf(version: MachineVersionRow | null): string {
  if (version === null || version.running_version === null) return 'Node version Unknown';
  if (version.behind === true) {
    return version.target_version === null
      ? `Node ${version.running_version} (behind)`
      : `Node ${version.running_version}, ${version.target_version} available`;
  }
  if (version.behind === false) return `Node ${version.running_version} (current)`;
  return `Node ${version.running_version}`;
}

/**
 * Moments in the samples Brunel already keeps where this machine was over one of
 * the limits it carries now. Read back rather than recorded: no new table, and a
 * dimension a sample says nothing about produces no event.
 */
function historyHoldBacks(
  samples: readonly MachineSampleRow[],
  limits: MachineCapacityLimits,
): MachineHoldBackEvent[] {
  const out: MachineHoldBackEvent[] = [];
  for (const row of samples) {
    const total = finite(row.memory_total_bytes);
    const free = finite(row.memory_free_bytes);
    if (total !== null && free !== null && total > 0) {
      const freePercent = Math.round((free / total) * 100);
      if (freePercent < limits.reserveMemoryPercent) {
        out.push({
          at: row.observed_at,
          dimension: 'memory',
          detail: `free memory ${freePercent}% under the ${limits.reserveMemoryPercent}% reserve`,
        });
      }
    }
    const videoTotal = finite(row.video_memory_total_bytes);
    const videoUsed = finite(row.video_memory_used_bytes);
    if (videoTotal !== null && videoUsed !== null && videoTotal > 0) {
      const freePercent = Math.round(((videoTotal - videoUsed) / videoTotal) * 100);
      if (freePercent < limits.reserveGraphicsPercent) {
        out.push({
          at: row.observed_at,
          dimension: 'video_memory',
          detail: `free video memory ${freePercent}% under the ${limits.reserveGraphicsPercent}% reserve`,
        });
      }
    }
    const cpu = finite(row.cpu_load_percent);
    if (cpu !== null && limits.cpuCeilingPercent !== null && cpu >= limits.cpuCeilingPercent) {
      out.push({
        at: row.observed_at,
        dimension: 'cpu',
        detail: `processor load ${cpu}% at or above the ${limits.cpuCeilingPercent}% ceiling`,
      });
    }
    const graphics = finite(row.graphics_util_percent);
    if (graphics !== null && limits.graphicsCeilingPercent !== null && graphics >= limits.graphicsCeilingPercent) {
      out.push({
        at: row.observed_at,
        dimension: 'graphics',
        detail: `graphics load ${graphics}% at or above the ${limits.graphicsCeilingPercent}% ceiling`,
      });
    }
  }
  return out;
}

/** One machine out of the built list, by the computer it belongs to. */
export function machineCapacityFor(
  machines: readonly MachineCapacity[],
  nodeId: string | null,
): MachineCapacity | null {
  if (nodeId === null) return null;
  return machines.find((machine) => machine.nodeId === nodeId) ?? null;
}

/**
 * The machines Resource Health and the Core Observer should say something about:
 * held back first, then nearing a limit. A machine nothing is known about is not
 * evidence of anything and is left out.
 */
export function machinesNeedingAttention(
  machines: readonly MachineCapacity[],
): readonly MachineCapacity[] {
  return machines.filter((machine) =>
    machine.stance === 'held_back' || machine.stance === 'at_seat_limit' || machine.nearingLimit);
}
